import { Link, router } from "@inertiajs/react"
import { Layers } from "lucide-react"
import { useIntl } from "react-intl"
import { TableCard } from "@/components/blocks/TableCard"
import { Badge } from "@/components/catalyst/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { useFormat } from "@/lib/format"
import type { DeckSummary } from "@/types/flashcards"

/**
 * Decks in a table card. The deck's title is a link stretched over the row, so
 * the whole row opens the deck; the review toggle sits above it.
 */
export function DeckList({
  decks,
  hrefFor,
  showStatus = false,
}: {
  decks: DeckSummary[]
  hrefFor: (deck: DeckSummary) => string
  showStatus?: boolean
}) {
  const intl = useIntl()
  const format = useFormat()
  return (
    <TableCard>
      <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
        <TableHead>
          <TableRow>
            <TableHeader>{intl.formatMessage({ id: "flashcards.list_deck" })}</TableHeader>
            <TableHeader className="hidden sm:table-cell">{intl.formatMessage({ id: "flashcards.list_cards" })}</TableHeader>
            {!showStatus && <TableHeader className="hidden sm:table-cell">{intl.formatMessage({ id: "flashcards.list_due" })}</TableHeader>}
            <TableHeader className="text-right">{showStatus ? intl.formatMessage({ id: "flashcards.list_status" }) : intl.formatMessage({ id: "flashcards.list_review" })}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {decks.map((deck) => (
            <TableRow key={deck.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Layers className="size-4 shrink-0 text-ink-muted" />
                  <div className="min-w-0">
                    <Link href={hrefFor(deck)} className="font-medium text-ink-display no-underline after:absolute after:inset-0 hover:text-ink-display">
                      {deck.title}
                    </Link>
                    {deck.description && <div className="max-w-xs truncate text-xs text-ink-muted">{deck.description}</div>}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden text-ink-muted sm:table-cell">{format.cardCount(deck.card_count)}</TableCell>
              {!showStatus && (
                <TableCell className="hidden sm:table-cell">
                  {deck.in_review && deck.due ? <Badge color="accent">{intl.formatMessage({ id: "flashcards.due_badge" }, { count: deck.due })}</Badge> : <span className="text-ink-muted">{intl.formatMessage({ id: "flashcards.none" })}</span>}
                </TableCell>
              )}
              <TableCell className="text-right">
                {showStatus ? (
                  <span className="inline-flex items-center gap-2">
                    {deck.ai_draft && <Badge color="amber">{intl.formatMessage({ id: "flashcards.ai_draft" })}</Badge>}
                    <Badge color={deck.status === "published" ? "accent" : "zinc"}>
                      {deck.status === "published" ? intl.formatMessage({ id: "flashcards.published" }) : intl.formatMessage({ id: "flashcards.draft" })}
                    </Badge>
                  </span>
                ) : deck.in_review !== undefined ? (
                  <span className="relative z-10 inline-flex">
                    <ReviewToggle deck={deck} />
                  </span>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  )
}

/** Switches a deck in or out of the user's daily review. */
export function ReviewToggle({ deck }: { deck: DeckSummary }) {
  const intl = useIntl()
  const included = deck.in_review ?? true
  return (
    <button
      type="button"
      className="toggle-button shrink-0"
      aria-pressed={included}
      title={included ? intl.formatMessage({ id: "flashcards.in_review_tip" }) : intl.formatMessage({ id: "flashcards.not_in_review_tip" })}
      onClick={() =>
        router.patch(`/decks/${deck.id}/review_selection`, { included: !included }, { preserveScroll: true })
      }
    >
      {included ? intl.formatMessage({ id: "flashcards.in_review" }) : intl.formatMessage({ id: "flashcards.not_in_review" })}
    </button>
  )
}
